import { heapAddEventProperties, heapTrackProperties } from '@m/utils/heap';

/**
 * Heap tracking utilities for ParkNow map page
 * Tracks user interactions, page views, and navigation flow
 */

/**
 * Generate manager dashboard URL for a site
 */
export const getSiteManagerUrl = (siteId: number | string): string => {
  return `https://manager.metropolis.io/sites/${siteId}/details`;
};

/**
 * Track page view when ParkNowContainer renders
 */
export const trackParkNowPageView = () => {
  heapTrackProperties('ParkNow Page View', {
    timestamp: Date.now(),
    'timestamp(utc)': new Date().toUTCString(),
  });
};

/**
 * Track when a user clicks the dashboard CTA to open the ParkNow map.
 */
export const trackDashboardCTAClick = () => {
  heapTrackProperties('Dashboard Find Parking Near You Click', {
    destination: '/park-now',
    timestamp: Date.now(),
  });
};

/**
 * Track when a user clicks the Locations item in the side menu.
 */
export const trackLocationsMenuClick = () => {
  heapTrackProperties('Side Menu Locations Click', {
    destination: '/park-now',
    timestamp: Date.now(),
  });
};

/**
 * Track page render stats including geolocation status
 */
export const trackParkNowPageRender = ({
  geolocationStatus,
  hasClosestRegion,
  initialLatitude,
  initialLongitude,
}: {
  geolocationStatus: 'success' | 'denied' | 'unavailable' | 'pending';
  hasClosestRegion: boolean;
  initialLatitude?: number;
  initialLongitude?: number;
}) => {
  heapTrackProperties('ParkNow Page Render', {
    geolocationStatus,
    hasClosestRegion,
    initialLatitude,
    initialLongitude,
    timestamp: Date.now(),
  });
};

/**
 * Track when a user clicks on a garage/zone pin on the map
 */
export const trackGaragePinClick = ({
  action,
  latitude,
  longitude,
  siteId,
  siteName,
}: {
  action: 'select' | 'deselect';
  latitude: number;
  longitude: number;
  siteId: number | string;
  siteName?: string;
}) => {
  heapTrackProperties('ParkNow Garage Pin Click', {
    action,
    latitude,
    longitude,
    siteId,
    siteManagerUrl: getSiteManagerUrl(siteId),
    siteName: siteName || '',
    timestamp: Date.now(),
  });

  // Add event properties for funnel analysis
  if (action === 'select') {
    heapAddEventProperties({
      lastSelectedSiteId: siteId,
      lastSelectedSiteName: siteName || '',
    });
  }
};

/**
 * Track when a user searches for a location
 */
export const trackLocationSearch = ({
  resultsCount,
  searchQuery,
}: {
  resultsCount: number;
  searchQuery: string;
}) => {
  heapTrackProperties('ParkNow Location Search', {
    resultsCount,
    searchQuery,
    timestamp: Date.now(),
  });
};

/**
 * Track when a user selects a location from search results
 */
export const trackLocationSelect = ({
  address,
  latitude,
  longitude,
}: {
  address: string;
  latitude: number;
  longitude: number;
}) => {
  heapTrackProperties('ParkNow Location Select', {
    address,
    latitude,
    longitude,
    timestamp: Date.now(),
  });
};

/**
 * Track when the location card is opened or closed
 */
export const trackLocationCardAction = ({
  action,
  siteId,
  siteName,
}: {
  action: 'open' | 'close';
  siteId: number | string;
  siteName?: string;
}) => {
  heapTrackProperties('ParkNow Location Card', {
    action,
    siteId,
    siteManagerUrl: getSiteManagerUrl(siteId),
    siteName: siteName || '',
    timestamp: Date.now(),
  });
};

/**
 * Track when sites are loaded on the map
 */
export const trackSitesLoaded = ({
  newSitesCount,
  searchRadius,
  sitesCount,
}: {
  newSitesCount: number;
  searchRadius: number;
  sitesCount: number;
}) => {
  heapTrackProperties('ParkNow Sites Loaded', {
    newSitesCount,
    searchRadius,
    sitesCount,
    timestamp: Date.now(),
  });
};

/**
 * Track when user types in the search bar
 */
export const trackSearchBarInput = ({
  action,
  searchQuery,
}: {
  action: 'typing' | 'clear' | 'focus';
  searchQuery: string;
}) => {
  heapTrackProperties('ParkNow Search Bar Input', {
    action,
    searchQuery,
    timestamp: Date.now(),
  });
};

/**
 * Track when user clicks the directions button
 */
export const trackDirectionsClick = ({
  address,
  platform,
  siteId,
  siteName,
}: {
  address: string;
  platform: 'apple_maps' | 'google_maps';
  siteId: number | string;
  siteName?: string;
}) => {
  heapTrackProperties('ParkNow Directions Click', {
    address,
    platform,
    siteId,
    siteManagerUrl: getSiteManagerUrl(siteId),
    siteName: siteName || '',
    timestamp: Date.now(),
  });
};
